import { cn } from '@/lib/utils';

/* OS HORÁRIOS DAS TRAVAS (Douglas 30/10/2026): o dia dela em uma lista, cada
   horário com o que aconteceu nele. Tramitação (desenho v5 aprovado): os
   horários marcados, montados por fn_bko_tramitacao_agenda_em. Contrato
   (maquete aprovada): as travas reais do dia com o livre entre elas, montadas
   por fn_bko_contrato_agenda_em, e depois do agora só a próxima checagem.
   Portabilidade (maquete aprovada): os quatro livres fixos, o almoço e as
   travas reais por fila, montados por fn_bko_portabilidade_agenda_em. A tela
   só desenha. */

export type ItemAgenda = {
  hora: string;
  ate?: string | null;
  tipo: 'livre' | 'almoco' | 'trava';
  fila?: 'instalacao' | 'consulta' | 'erro_solar' | 'contrato' | Porta;
  rotulo: string;
  detalhe?: string | null;
  pedidos?: number | null;
  restam?: number | null;
  abriu?: string | null;
  fechou?: string | null;
  estado?: 'aberta' | 'pausada' | 'sem_atraso' | 'ficou' | 'feita' | 'destravou' | 'junto' | 'na_espera' | 'a_seguir';
  junto_de?: string | null;
};

type Porta = 'inspecao' | 'apresentacao' | 'bilhete' | 'sms' | 'agendada' | 'divergente' | 'complementar';

export type DiaAgenda = { inicio?: string | null; fim?: string | null; proxima?: string | null; rotulo?: string | null };

const HACHURA = 'bg-[repeating-linear-gradient(135deg,#5B0A0D_0_3px,#B4808C_3px_6px)] text-white dark:bg-[repeating-linear-gradient(135deg,#C24545_0_3px,#5E2229_3px_6px)]';

const qtd = (n: number) => (n === 1 ? '1 pedido' : `${n} pedidos`);

function naoDestravou(i: ItemAgenda) {
  const n = i.pedidos ?? 0;
  return i.restam != null && i.restam > 0 && i.restam < n ? `não destravou · faltaram ${i.restam} de ${n}` : `não destravou · ${qtd(n)}`;
}

function Estado({ i }: { i: ItemAgenda }) {
  const pedidos = qtd(i.pedidos ?? 0);
  switch (i.estado) {
    case 'aberta':
      return <span className="rounded-md bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] px-2 py-0.5 text-[11px] font-bold text-white dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]">travada agora · {pedidos}</span>;
    case 'pausada':
      return <span className="rounded-md border border-[#7A2235] px-2 py-0.5 text-[11px] font-bold text-[#7A2235] dark:border-[#C87484] dark:text-[#C87484]">pausada · {pedidos}</span>;
    case 'feita':
      return <span className="text-[11px] font-semibold text-[#075E54] dark:text-[#25D366]">feita às {i.fechou} · {pedidos}</span>;
    case 'destravou':
      return <span className="text-[11px] font-semibold text-[#075E54] dark:text-[#25D366]">destravou às {i.fechou} · {pedidos}</span>;
    case 'sem_atraso':
      return <span className="text-[11px] font-semibold text-[#075E54] dark:text-[#25D366]">nada atrasado, não travou</span>;
    case 'ficou':
      return <span className={cn('rounded-md px-2 py-0.5 text-[11px] font-bold', HACHURA)}>{naoDestravou(i)}</span>;
    case 'junto':
      return <span className="text-[11px] text-muted-foreground">entrou na das {i.junto_de}</span>;
    case 'na_espera':
      return <span className="text-[11px] font-semibold text-[#7A2235] dark:text-[#C87484]">abre quando terminar a atual</span>;
    default:
      return <span className="text-[11px] text-muted-foreground">ainda não chegou a hora</span>;
  }
}

/* O MAPA DO DIA (Douglas 30/10/2026: "não está a maquete aquela visual"): a
   linha das 8h ao fim do expediente do desenho aprovado. Na Tramitação cada
   faixa começa no horário da trava e horários iguais dividem a faixa na ordem
   da prioridade; no Contrato cada faixa tem começo e fim reais (ate). */
const minutos = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));

const COR_FAIXA: Record<string, string> = {
  instalacao: 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]',
  contrato: 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]',
  consulta: 'bg-[#9D4459] text-white dark:bg-[#7A2235]',
  erro_solar: 'bg-[#B4808C] text-[#2E2019] dark:bg-[#5E2229] dark:text-[#F1EDE7]',
  inspecao: 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]',
  apresentacao: 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]',
  bilhete: 'bg-[#7A2235] text-white dark:bg-[#7A2235]',
  sms: 'bg-[#9D4459] text-white dark:bg-[#9D4459]',
  agendada: 'bg-[#B4808C] text-[#2E2019] dark:bg-[#5E2229] dark:text-[#F1EDE7]',
  divergente: 'bg-[#6B5A4D] text-white dark:bg-[#6B5A4D]',
  complementar: 'bg-[#8A7576] text-white dark:bg-[#8A7576]',
  livre: 'bg-[#EDE8E0] text-[#6B5A4D] dark:bg-[#161010] dark:text-[#B5A8A8]',
  almoco: 'border border-dashed border-[#A6907B] bg-background text-[#6B5A4D] dark:border-[#8A7576] dark:text-[#B5A8A8]',
};

const CURTO: Record<string, string> = {
  instalacao: 'Instalação', consulta: 'Consulta', erro_solar: 'Erro Solar', contrato: 'Trava', livre: 'Livre', almoco: 'Almoço',
  inspecao: 'Inspeção', apresentacao: 'Inspeção', bilhete: 'Bilhete', sms: 'SMS', agendada: 'Agendada', divergente: 'Divergente', complementar: 'Complementar',
};
const LEGENDA: Record<string, string> = {
  instalacao: 'Instalação', consulta: 'Volta de consulta', erro_solar: 'Erro Solar', contrato: 'Travada', livre: 'Livre',
  inspecao: 'Inspeção', apresentacao: 'Inspeção', bilhete: 'Bilhete / NF', sms: 'SMS', agendada: 'Agendada', divergente: 'Divergente', complementar: 'Complementar',
};
const PORTA = ['inspecao', 'apresentacao', 'bilhete', 'sms', 'agendada', 'divergente', 'complementar'];

type Faixa = { ini: number; fim: number; chave: string; item: ItemAgenda };

const chaveDe = (i: ItemAgenda) => (i.tipo === 'trava' && i.estado !== 'sem_atraso' ? i.fila ?? 'livre' : i.tipo === 'trava' ? 'livre' : i.tipo);

function faixasDoDia(agenda: ItemAgenda[], dia: DiaAgenda) {
  const ini = dia.inicio ? minutos(dia.inicio) : minutos(agenda[0].hora);
  const fim = dia.fim ? minutos(dia.fim) : minutos(agenda[agenda.length - 1].hora) + 30;
  if (agenda.length > 0 && agenda.every((i) => i.ate)) {
    const faixas = agenda.map((i) => ({ ini: minutos(i.hora), fim: minutos(i.ate as string), chave: chaveDe(i), item: i }));
    return { ini, fim, faixas: [...faixas.filter((f) => f.chave !== 'almoco'), ...faixas.filter((f) => f.chave === 'almoco')] };
  }
  const horas = [...new Set(agenda.map((i) => i.hora))];
  const faixas: Faixa[] = [];
  horas.forEach((h, n) => {
    const de = minutos(h);
    const ate = n + 1 < horas.length ? minutos(horas[n + 1]) : fim;
    const juntos = agenda.filter((i) => i.hora === h);
    const passo = (ate - de) / juntos.length;
    juntos.forEach((i, k) => faixas.push({ ini: de + k * passo, fim: de + (k + 1) * passo, chave: chaveDe(i), item: i }));
  });
  return { ini, fim, faixas };
}

function rotuloFaixa(f: Faixa, largura: number) {
  if (f.chave !== 'contrato' && !PORTA.includes(f.chave)) return largura >= 3 ? CURTO[f.chave] : null;
  const n = f.item.pedidos ?? 0;
  if (largura < 9) return largura >= 2.5 ? String(n) : null;
  return f.item.estado === 'ficou' ? `Não destravou · ${n}` : `${CURTO[f.chave]} · ${n}`;
}

function agoraBrt() {
  const p = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'America/Sao_Paulo' }).format(new Date());
  return minutos(p);
}

export function MapaDoDia({ agenda, dia = {}, className }: { agenda: ItemAgenda[]; dia?: DiaAgenda; className?: string }) {
  if (agenda.length === 0 && !dia.fim) return null;
  const { ini, fim, faixas } = faixasDoDia(agenda, dia);
  const total = fim - ini;
  const pct = (m: number) => `${((m - ini) / total) * 100}%`;
  const agora = agoraBrt();
  const proxima = dia.proxima ? minutos(dia.proxima) : null;
  const ticks = Array.from({ length: Math.floor((fim - ini) / 60) + 1 }, (_, k) => Math.ceil(ini / 60) * 60 + k * 60).filter((m) => m <= fim);
  const legenda = ['instalacao', 'consulta', 'erro_solar', 'contrato', ...PORTA.filter((c) => c !== 'apresentacao'), 'livre']
    .filter((c) => faixas.some((f) => f.chave === c || (c === 'inspecao' && f.chave === 'apresentacao')));
  const temHachura = faixas.some((f) => f.item.estado === 'ficou');
  return (
    <div className={className}>
      <div className="relative h-4">
        {ticks.map((m) => (
          <span key={m} className="absolute -translate-x-1/2 text-[10px] tabular-nums text-muted-foreground first:translate-x-0 last:-translate-x-full" style={{ left: pct(m) }}>{m / 60}h</span>
        ))}
      </div>
      <div className="relative mt-1 h-10 rounded-[4px] border border-dashed border-border">
        {faixas.map((f) => {
          const largura = ((f.fim - f.ini) / total) * 100;
          const rotulo = rotuloFaixa(f, largura);
          return (
            <div key={`${f.item.hora}-${f.chave}-${f.ini}`}
              title={`${f.item.hora}${f.item.ate ? `–${f.item.ate}` : ''} · ${f.item.rotulo}${f.item.detalhe ? ` · ${f.item.detalhe}` : ''}`}
              className={cn('absolute -inset-y-px flex items-center justify-center overflow-hidden rounded-[4px] border-[1.5px] border-background text-center text-[9.5px] font-semibold leading-[1.1] tracking-[-0.1px]',
                f.item.estado === 'ficou' ? HACHURA : COR_FAIXA[f.chave], f.item.estado === 'aberta' && 'ring-2 ring-[#2E2019] dark:ring-[#F1EDE7]')}
              style={{ left: pct(f.ini), width: `${largura}%` }}>
              {rotulo && <span className="line-clamp-2">{rotulo}</span>}
            </div>
          );
        })}
        {!dia.rotulo && agora > ini && agora < fim && (
          <span className="absolute -inset-y-1 w-[2px] rounded bg-[#2E2019] dark:bg-[#F1EDE7]" style={{ left: pct(agora) }} title="agora" />
        )}
      </div>
      {proxima != null && proxima >= ini && proxima < fim && (
        <div className="relative mt-1 h-4">
          <span className={cn('absolute whitespace-nowrap text-[10.5px] font-semibold text-[#7A2235] dark:text-[#C87484]', proxima - ini > total * 0.8 ? '-translate-x-full' : proxima - ini > total * 0.1 && '-translate-x-1/2')}
            style={{ left: pct(proxima) }}>
            {proxima === 540 && faixas.every((f) => f.chave !== 'contrato') ? 'primeira checagem às 09:00' : `próxima checagem às ${dia.proxima}`}
          </span>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {legenda.map((c) => (
          <span key={c} className="inline-flex items-center gap-1.5"><span className={cn('h-2.5 w-2.5 rounded-[3px]', COR_FAIXA[c])} />{LEGENDA[c]}</span>
        ))}
        <span className="inline-flex items-center gap-1.5"><span className={cn('h-2.5 w-2.5 rounded-[3px]', COR_FAIXA.almoco)} />Almoço</span>
        {temHachura && <span className="inline-flex items-center gap-1.5"><span className={cn('h-2.5 w-2.5 rounded-[3px]', HACHURA)} />Não destravou até o fim do dia</span>}
      </div>
    </div>
  );
}

export function AgendaTramitacao({ agenda, dia, className }: { agenda: ItemAgenda[]; dia?: DiaAgenda; className?: string }) {
  if (agenda.length === 0 && !dia?.fim) {
    return <p className={cn('text-[12px] text-muted-foreground', className)}>Hoje não tem horário de trava (fim de semana).</p>;
  }
  return (
    <div className={className}>
      {dia?.rotulo && (
        <p className="mb-2 text-[12px] text-muted-foreground"><span className="font-semibold text-foreground">{dia.rotulo}</span> · o dia de hoje começa às 8h; até lá o quadro mostra o último dia útil.</p>
      )}
      <MapaDoDia agenda={agenda} dia={dia} className="mb-3" />
      <div className="divide-y divide-border">
        {agenda.length === 0 && (
          <p className="py-2 text-[12.5px] text-muted-foreground">O expediente ainda não começou. A primeira checagem é às 09:00.</p>
        )}
        {agenda.map((i) => (
          <div key={`${i.hora}-${i.ate ?? ''}-${i.fila ?? i.tipo}`} className="grid min-h-9 grid-cols-[46px_minmax(0,1fr)_auto] items-center gap-3 py-1">
            <span className="font-display text-[13px] font-bold tabular-nums text-foreground">{i.hora}</span>
            <span className="min-w-0 truncate text-[12.5px]" title={i.detalhe ? `${i.rotulo} · ${i.detalhe}` : i.rotulo}>
              <span className={cn('font-semibold', i.tipo === 'trava' ? 'text-foreground' : 'text-muted-foreground')}>{i.rotulo}</span>
              {i.detalhe && <span className="text-muted-foreground"> · {i.detalhe}</span>}
            </span>
            {i.tipo === 'trava' ? <Estado i={i} /> : <span />}
          </div>
        ))}
      </div>
    </div>
  );
}
